import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { whatsNewUpdates, formatUpdateDate } from '../assets/whatsNewData';

const WhatsNewPopup = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  const [selectedUpdate, setSelectedUpdate] = useState(null);

  // Handle window resize to detect desktop/mobile
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen && isDesktop) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when panel is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.bottom = '0';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.bottom = '';
    };
  }, [isOpen, onClose, isDesktop]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen && isDesktop) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose, isDesktop]);

  const handleCardClick = (update) => {
    if (update.detailedContent) {
      setSelectedUpdate(update);
    } else if (update.link) {
      if (update.link.startsWith('http')) {
        window.open(update.link, '_blank');
      } else {
        navigate(update.link);
        onClose();
      }
    }
  };

  const handleBackToList = () => {
    setSelectedUpdate(null);
  };

  // Use all updates since we removed search
  const filteredUpdates = whatsNewUpdates;

  if (!isOpen || !isDesktop) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div 
        className="fixed inset-0 bg-black/20"
        style={{ zIndex: 2147483646 }}
        onClick={onClose}
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
        initial={{ opacity: 0 }}
        animate={{ opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      />
      
      {/* Side Panel - Desktop Only */}
      <motion.div
        ref={panelRef}
        className={`fixed top-4 right-4 bottom-4 bg-white rounded-xl border border-gray-200 hidden md:flex md:flex-col ${
          selectedUpdate ? 'w-[520px]' : 'w-[420px]'
        }`}
        style={{ 
          maxWidth: selectedUpdate ? 'min(520px, 90vw)' : 'min(420px, 85vw)',
          zIndex: 2147483647
        }}
        initial={{ 
          x: '100%',
          opacity: 0,
          scale: 0.95
        }}
        animate={isOpen ? { 
          x: 0,
          opacity: 1,
          scale: 1,
          width: selectedUpdate ? 520 : 420
        } : {
          x: '100%',
          opacity: 0,
          scale: 0.95
        }}
        transition={{
          type: "tween",
          ease: [0.25, 0.46, 0.45, 0.94],
          duration: 0.35
        }}
      >
        {/* Header with animated background */}
        <div className="relative flex-shrink-0 rounded-t-xl overflow-hidden">
          {/* Animated radial background gradient */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 120% 140% at 50% 0%,rgb(249, 159, 63), rgba(255,253,99,0.0) 70%)",
              filter: "saturate(120%)",
            }}
            initial={{ opacity: 0.8 }}
            animate={{ opacity: [0.7, 1, 0.7] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Title and close button */}
          <div className="flex items-center p-6 relative z-10">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {selectedUpdate && (
                <button
                  onClick={handleBackToList}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-full transition-colors flex-shrink-0"
                >
                  <ChevronLeft size={20} />
                </button>
              )}
              <h2 className="text-2xl font-bold text-gray-900 truncate font-inter">
                {selectedUpdate ? selectedUpdate.title : "What's new in PollBolt"}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-full transition-colors flex-shrink-0 ml-4"
            >
              <X size={22} />
            </button>
          </div>



          {/* Date for selected update */}
          {selectedUpdate && selectedUpdate.date && (
            <div className="px-6 pb-6 relative z-10">
              <p className="text-sm text-gray-500 font-inter">{selectedUpdate.date}</p>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {selectedUpdate ? (
            /* Detailed View */
            <motion.div
              className="p-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6 font-inter">
                  {selectedUpdate.detailedContent.description}
                </p>

                {/* Demo Video */}
                {selectedUpdate.detailedContent.demoVideo && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 font-inter">See it in action:</h4>
                    <div className="relative rounded-lg overflow-hidden bg-gray-100">
                      <video
                        src={selectedUpdate.detailedContent.demoVideo}
                        autoPlay
                        loop
                        muted
                        controls
                        className="w-full h-auto max-h-80 object-cover"
                        preload="metadata"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                )}
                
                {selectedUpdate.detailedContent.benefits && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 font-inter">Here's how this new feature benefits you:</h4>
                    <ul className="space-y-3">
                      {selectedUpdate.detailedContent.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          <span className="text-gray-700 text-sm leading-relaxed font-inter">
                            <strong className="text-gray-900">{benefit.split(':')[0]}:</strong>
                            {benefit.split(':')[1]}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            /* Updates List */
            filteredUpdates.map((update, index) => (
              <motion.div
                key={update.id}
                onClick={() => handleCardClick(update)}
                className="rounded-lg p-5 hover:bg-gray-50 transition-all duration-200 cursor-pointer border-b border-gray-100 last:border-b-0"
                initial={{ 
                  opacity: 0, 
                  y: 10,
                  scale: 0.98
                }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: 1
                }}
                transition={{ 
                  delay: isOpen ? index * 0.03 : 0,
                  duration: 0.4,
                  ease: "easeOut"
                }}
              >
                <h3 className="font-semibold text-gray-900 mb-2 leading-tight text-base font-inter">
                  {update.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed font-inter">
                  {update.description}
                </p>
              </motion.div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50/30 flex-shrink-0 rounded-b-xl">
          <p className="text-xs text-gray-500 text-center font-inter">
            Stay updated with the latest features and improvements
          </p>
        </div>
      </motion.div>
    </>
  );
};

export default WhatsNewPopup;